import { CarAdmissionType } from '@/types/car/admission-type.type.ts';
import { CarBodyType } from '@/types/car/car-body-type.type.ts';
import { CarEngineType } from '@/types/car/car-engine-type.type.ts';
import { CarNdsType } from '@/types/car/car-nds-type.ts';
import { CarTransmissionType } from '@/types/car/car-transmission-type.type.ts';
import { CarDamageCreateForm } from '@/types/car/car-damage-create-form.type.ts';
import {
    ComplectationOptionsUICreateForm,
    makeComplectationOptionsEmptyUICreateForm,
    mapComplectationOptionsUICreateFormToRequestCreateForm,
} from '@/types/complectation.type.ts';
import {
    CarCreateForm,
    CarCreateFormDocuments,
    CarCreateFormPhoto,
    CarCreateFormPhotos,
} from '@/types/car/car-create-form.type.ts';
import { CarDriveType } from '@/types/car/car-drive-type.type.ts';
import { CarPhotoType } from '@/types/car/car-photo-type.type.ts';

export type CarUICreateForm = {
    brand_id?: number,
    model_id?: number,
    model_code_id?: number,
    vin: string,
    admission_type?: CarAdmissionType,
    pts_number: string,
    body_type?: CarBodyType,
    body_color: string,
    engine_type?: CarEngineType,
    drive_type?: CarDriveType,
    mileage: string,
    production_year?: number,
    engine_power: string,
    engine_volume: number,
    start_price: string,
    nds_type?: CarNdsType,
    transmission_type?: CarTransmissionType,
    additional_equipment?: string,
    dealer_id?: number,
    complectation_id?: number,
    owners_count?: number,
    months_with_last_owner?: number,
    is_not_stolen: boolean,
    is_not_pledged: boolean,
    not_exist_dtp: boolean,
    comment: string,
    tags: string,
    was_owned_by_individual: boolean,
    options: ComplectationOptionsUICreateForm,
    car_damages: CarDamageCreateForm[],
    photos: CarCreateFormPhotos,
    additional_photos: CarCreateFormPhoto[],
    documents: CarCreateFormDocuments,
    car_videos: File[],
};

export enum CarUICreateFormStep {
    Common = 1,
    Characteristics = 2,
    AdditionalParameters = 3,
    Complectation = 4,
    Media = 5,
    Damages = 6,
    Legal = 7,
}

export function makeCarEmptyUICreateForm(): CarUICreateForm {
    return {
        brand_id: undefined,
        model_id: undefined,
        model_code_id: undefined,
        vin: '',
        admission_type: undefined,
        pts_number: '',
        body_type: undefined,
        body_color: '',
        engine_type: undefined,
        drive_type: undefined,
        mileage: '',
        production_year: undefined,
        engine_power: '',
        engine_volume: 0.8,
        start_price: '',
        nds_type: undefined,
        transmission_type: undefined,
        additional_equipment: '',
        dealer_id: undefined,
        complectation_id: undefined,
        owners_count: undefined,
        months_with_last_owner: undefined,
        is_not_stolen: false,
        is_not_pledged: false,
        not_exist_dtp: false,
        comment: '',
        tags: '',
        was_owned_by_individual: false,
        options: makeComplectationOptionsEmptyUICreateForm(),
        car_damages: [],
        photos: {
            front_left: { file: null, rotation: 0 },
            rear_right: { file: null, rotation: 0 },
            interior_front: { file: null, rotation: 0 },
            interior_rear: { file: null, rotation: 0 },
            left: { file: null, rotation: 0 },
            right: { file: null, rotation: 0 },
            dashboard: { file: null, rotation: 0 },
            trunk: { file: null, rotation: 0 },
            wheel: { file: null, rotation: 0 },
            under_hood: { file: null, rotation: 0 },
        },
        additional_photos: [{ file: null, rotation: 0 }, { file: null, rotation: 0 }],
        documents: {},
        car_videos: [],
    };
}

export function mapCarUICreateFormToRequestCreateForm(uiCreateForm: CarUICreateForm): CarCreateForm {
    const createForm: CarCreateForm = {
        vin: uiCreateForm.vin,
        pts_number: uiCreateForm.pts_number,
        body_color: uiCreateForm.body_color,
        is_stolen: uiCreateForm.is_not_stolen ? 0 : 1,
        is_pledged: uiCreateForm.is_not_pledged ? 0 : 1,
        exist_dtp: uiCreateForm.not_exist_dtp ? 0 : 1,
        was_owned_by_individual: uiCreateForm.was_owned_by_individual ? 1 : 0,
        options: mapComplectationOptionsUICreateFormToRequestCreateForm(uiCreateForm.options),
    };

    if (uiCreateForm.brand_id !== undefined) {
        createForm.brand_id = uiCreateForm.brand_id;
    }

    if (uiCreateForm.model_id !== undefined) {
        createForm.model_id = uiCreateForm.model_id;
    }

    if (uiCreateForm.model_code_id !== undefined) {
        createForm.model_code_id = uiCreateForm.model_code_id;
    }

    if (uiCreateForm.admission_type !== undefined) {
        createForm.admission_type = uiCreateForm.admission_type;
    }

    if (uiCreateForm.body_type !== undefined) {
        createForm.body_type = uiCreateForm.body_type;
    }

    if (uiCreateForm.engine_type !== undefined) {
        createForm.engine_type = uiCreateForm.engine_type;
    }

    if (uiCreateForm.drive_type !== undefined) {
        createForm.drive_type = uiCreateForm.drive_type;
    }

    if (uiCreateForm.mileage !== undefined && uiCreateForm.mileage.length > 0) {
        createForm.mileage = parseInt(uiCreateForm.mileage);
    }

    if (uiCreateForm.production_year !== undefined) {
        createForm.production_year = uiCreateForm.production_year;
    }

    if (uiCreateForm.engine_power !== undefined && uiCreateForm.engine_power.length > 0) {
        createForm.engine_power = parseInt(uiCreateForm.engine_power);
    }

    if (uiCreateForm.engine_volume !== undefined) {
        createForm.engine_volume = uiCreateForm.engine_volume;
    }

    if (uiCreateForm.start_price !== undefined && uiCreateForm.start_price.length > 0) {
        createForm.start_price = parseInt(uiCreateForm.start_price);
    }

    if (uiCreateForm.nds_type !== undefined) {
        createForm.nds_type = uiCreateForm.nds_type;
    }

    if (uiCreateForm.transmission_type !== undefined) {
        createForm.transmission_type = uiCreateForm.transmission_type;
    }

    if (uiCreateForm.additional_equipment !== undefined && uiCreateForm.additional_equipment.length > 0) {
        createForm.additional_equipment = uiCreateForm.additional_equipment;
    }

    if (uiCreateForm.dealer_id !== undefined) {
        createForm.dealer_id = uiCreateForm.dealer_id;
    }

    if (uiCreateForm.complectation_id !== undefined) {
        createForm.complectation_id = uiCreateForm.complectation_id;
    }

    if (uiCreateForm.owners_count !== undefined && Number(uiCreateForm.owners_count)) {
        createForm.owners_count = Number(uiCreateForm.owners_count);
    }

    if (uiCreateForm.months_with_last_owner !== undefined && Number(uiCreateForm.months_with_last_owner)) {
        createForm.months_with_last_owner = Number(uiCreateForm.months_with_last_owner);
    }

    if (uiCreateForm.comment !== undefined && uiCreateForm.comment.length > 0) {
        createForm.comment = uiCreateForm.comment;
    }

    if (uiCreateForm.tags !== undefined && uiCreateForm.tags.length > 0) {
        createForm.tags = uiCreateForm.tags;
    }

    if (uiCreateForm.car_damages.length > 0) {
        createForm.car_damages = uiCreateForm.car_damages;
    }

    const photos: CarCreateFormPhotos = {};
    Object.entries(uiCreateForm.photos).forEach(([type, photo]) => {
        if (photo.file !== undefined && photo.file !== null) {
            photos[type as CarPhotoType] = { ...photo };
        }
    });

    if (Object.keys(photos).length > 0) {
        createForm.photos = photos;
    }

    const additionalPhotos: CarCreateFormPhoto[] = [];
    uiCreateForm.additional_photos.forEach((photo) => {
        if (photo.file !== undefined && photo.file !== null) {
            additionalPhotos.push({ ...photo });
        }
    });

    if (additionalPhotos.length > 0) {
        createForm.additional_photos = additionalPhotos;
    }

    if (Object.keys(uiCreateForm.documents).length > 0) {
        createForm.documents = uiCreateForm.documents;
    }

    if (uiCreateForm.car_videos.length > 0) {
        createForm.car_videos = uiCreateForm.car_videos;
    }

    return createForm;
}
